/**
 * Supplier identity for the RESELLER-facing hotel surfaces (CTQ trip builder +
 * à-la-carte search). Shows which wholesaler a hotel/rate comes from — RateHawk,
 * Nuitée, Expedia, … — so the reseller knows who they're booking through and can
 * compare the same hotel's price across suppliers. Never shown to customers.
 *
 * Text/colour badges for now (clean + no asset dependency); swap in real logos
 * later by pointing `logo` at an image. The provider KEY is the supplier tag that
 * rides on every rate (rate.provider) + booking row (HotelSource).
 */

// Canonical display + palette per supplier. Unknown providers fall back to a
// capitalized slate badge so a newly-added supplier still renders sanely.
const PROVIDERS = {
  ratehawk:      { label: 'RateHawk',    fg: '#0f766e', bg: '#f0fdfa', bd: '#99f6e4' },
  nuitee:        { label: 'Nuitée',      fg: '#4338ca', bg: '#eef2ff', bd: '#c7d2fe' },
  expedia:       { label: 'Expedia',     fg: '#a16207', bg: '#fefce8', bd: '#fde68a' },
  expedia_rapid: { label: 'Expedia',     fg: '#a16207', bg: '#fefce8', bd: '#fde68a' },
  bookingcom:    { label: 'Booking.com', fg: '#1d4ed8', bg: '#eff6ff', bd: '#bfdbfe' },
}

export function providerMeta(provider) {
  const key = String(provider || '').toLowerCase()
  if (PROVIDERS[key]) return { key, ...PROVIDERS[key] }
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Supplier'
  return { key, label, fg: '#475569', bg: '#f1f5f9', bd: '#e2e8f0' }
}

export default function ProviderBadge({ provider, size = 'sm', title, style }) {
  const m = providerMeta(provider)
  const dims = size === 'xs'
    ? { fontSize: 9.5, padding: '1px 5px', borderRadius: 4 }
    : { fontSize: 10.5, padding: '2px 7px', borderRadius: 5 }
  return (
    <span title={title || m.label}
      style={{ display: 'inline-block', fontWeight: 700, whiteSpace: 'nowrap',
        color: m.fg, background: m.bg, border: `1px solid ${m.bd}`, ...dims, ...style }}>
      {m.label}
    </span>
  )
}

/**
 * Collapse a hotel's rates (each tagged rate.provider + rate.total, per-room)
 * into one entry per provider, cheapest-first, so the UI can show "cheapest
 * headline + also available from" without re-deriving it everywhere.
 * Returns [{ provider, label, cheapest, rateCount }] sorted by cheapest asc.
 */
export function ratesByProvider(rates) {
  const by = new Map()
  for (const r of rates || []) {
    const p = String(r.provider || 'expedia').toLowerCase()
    const total = Number(r.total)
    const cur = by.get(p) || { provider: p, label: providerMeta(p).label, cheapest: null, rateCount: 0 }
    cur.rateCount += 1
    if (Number.isFinite(total)) cur.cheapest = cur.cheapest == null ? total : Math.min(cur.cheapest, total)
    by.set(p, cur)
  }
  return [...by.values()].sort((a, b) => (a.cheapest ?? Infinity) - (b.cheapest ?? Infinity))
}
